import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';

import { api, queryString } from '@/lib/api';
import type {
  CreatePropertyCategoryInput,
  CreatePropertyTaskInput,
  PropertyCategoryDetail,
  PropertyCategoryView,
  PropertyTaskView,
  UpdatePropertyCategoryInput,
  UpdatePropertyTaskInput,
} from '@shared/schemas/catalog';
import type { ActivityFilter, ListResponse } from '@shared/schemas/pagination';

/*
 * The queries and mutations behind /admin/property-categories.
 *
 * Separated from the page so the page is layout and the cache rules are here in
 * one place. Every mutation invalidates the same root key rather than patching
 * the cache by hand: a checklist reorder changes sort orders the server
 * assigned, and a hand-patched cache would show the order the browser guessed.
 */

const ROOT = ['admin', 'property-categories'] as const;

export interface CategoryListParams {
  page: number;
  search: string;
  activity: ActivityFilter;
  /** Defaults to the server's 25. A dropdown over the whole list asks for more. */
  pageSize?: number;
}

export function usePropertyCategories(params: CategoryListParams) {
  return useQuery({
    queryKey: [...ROOT, 'list', params],
    queryFn: () =>
      api.get<ListResponse<PropertyCategoryView>>(
        `/api/admin/property-categories${queryString({
          page: params.page,
          search: params.search,
          activity: params.activity,
          pageSize: params.pageSize,
        })}`,
      ),
    // Keeps the previous page on screen while the next one loads, so paging
    // does not flash a skeleton over a list that is about to look almost the
    // same.
    placeholderData: (previous) => previous,
  });
}

/**
 * One category and its checklist.
 *
 * `enabled` is what makes the expand-to-see-tasks pattern cheap: the row only
 * fetches its tasks when somebody opens it, and eight collapsed categories cost
 * one request between them rather than nine.
 */
export function usePropertyCategory(id: string | null) {
  return useQuery({
    queryKey: [...ROOT, 'detail', id],
    queryFn: () => api.get<PropertyCategoryDetail>(`/api/admin/property-categories/${id ?? ''}`),
    enabled: id !== null,
  });
}

function invalidate(client: QueryClient): Promise<void> {
  return client.invalidateQueries({ queryKey: ROOT });
}

export function useCreateCategory() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: CreatePropertyCategoryInput) =>
      api.post<PropertyCategoryDetail>('/api/admin/property-categories', input),
    onSuccess: () => invalidate(client),
  });
}

export function useUpdateCategory(id: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdatePropertyCategoryInput) =>
      api.patch<PropertyCategoryDetail>(`/api/admin/property-categories/${id}`, input),
    onSuccess: () => invalidate(client),
  });
}

export function useSetCategoryActive(id: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (isActive: boolean) =>
      api.post<PropertyCategoryDetail>(
        `/api/admin/property-categories/${id}/${isActive ? 'reactivate' : 'deactivate'}`,
      ),
    onSuccess: () => invalidate(client),
  });
}

export function useCreateTask(categoryId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: CreatePropertyTaskInput) =>
      api.post<PropertyTaskView>(`/api/admin/property-categories/${categoryId}/tasks`, input),
    onSuccess: () => invalidate(client),
  });
}

export function useUpdateTask(categoryId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ taskId, input }: { taskId: string; input: UpdatePropertyTaskInput }) =>
      api.patch<PropertyTaskView>(
        `/api/admin/property-categories/${categoryId}/tasks/${taskId}`,
        input,
      ),
    onSuccess: () => invalidate(client),
  });
}

export function useDeleteTask(categoryId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (taskId: string) =>
      api.delete<void>(`/api/admin/property-categories/${categoryId}/tasks/${taskId}`),
    onSuccess: () => invalidate(client),
  });
}

export function useReorderTasks(categoryId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (taskIds: string[]) =>
      api.put<{ data: PropertyTaskView[] }>(
        `/api/admin/property-categories/${categoryId}/tasks/order`,
        { taskIds },
      ),
    onSuccess: () => invalidate(client),
  });
}
